import logoColour from '../../assets/brand/safaqat24-logo.png';
import logoOnDark from '../../assets/brand/safaqat24-logo-ondark.png';
import markColour from '../../assets/brand/safaqat24-mark-sq.png';
import markOnDark from '../../assets/brand/safaqat24-mark-sq-ondark.png';

/**
 * Safaqat 24 logo — THE DESIGNER'S ARTWORK. THEME.md §7.
 *
 * This component used to draw an inline SVG *interpretation* of the mark:
 * three flat rounded rectangles with a pennant and type set in webfonts. It
 * was a placeholder because no vector existed, it sat in the header of all 127
 * pages, and it was the single most off-brand thing on the site — the real
 * mark has OUTLINED towers, a calligraphic Arabic wordmark and two hand-drawn
 * red swooshes, none of which can be rebuilt by hand.
 *
 * The owner supplied `safaqat24-mark.png` on 2026-09-17. The four assets here
 * are derived from it by `scripts/prep-logo.mjs` — run that after any change
 * to the source artwork; do not hand-edit the PNGs.
 *
 * Four assets, two axes:
 *
 *                     light surface          deep-green surface
 *   full lockup       safaqat24-logo         safaqat24-logo-ondark
 *   square mark       safaqat24-mark-sq      safaqat24-mark-sq-ondark
 *
 * The `-ondark` pair maps the artwork's dark green to white and drops the
 * enclosed tower interiors to transparent, so the towers read as white
 * outlines on green. Brand green on `#002A23` is 1.2:1 — the colour artwork is
 * simply invisible there. Yellow, blue and red are left exactly as drawn.
 *
 * DARK MODE. `tone="colour"` renders BOTH files and swaps them with a `dark:`
 * variant, because a "light surface" becomes a dark one under `.dark` and the
 * dark-green artwork would disappear. `tone="onDark"` is for a surface that is
 * deep green in BOTH themes — the footer — so it renders one file only.
 *
 * Why raster and not SVG: the source is raster. Tracing the calligraphy to
 * vector is a design job, not a build step, and a bad trace looks worse than a
 * crisp bitmap. The assets are 2x their largest on-screen size, so they are
 * sharp on retina; the full lockup is ~133 KB, which is a quarter of what the
 * hero photograph it replaced cost.
 */

type Tone = 'colour' | 'onDark';

/** Shared img props. A titled logo is an image; an untitled one is decoration. */
function imgProps(title: string | undefined) {
  return title
    ? ({ alt: title, role: 'img' as const })
    : ({ alt: '', 'aria-hidden': true as const });
}

/** The full lockup: towers, «صفقات», Safaqat and the red 24 with its swoosh. */
export default function SafaqatLogo({
  tone = 'colour',
  className = '',
  title = 'صفقات 24 - Safaqat 24',
}: {
  tone?: Tone;
  className?: string;
  title?: string;
}) {
  if (tone === 'onDark') {
    return <img src={logoOnDark} className={className} draggable={false} {...imgProps(title)} />;
  }
  return (
    <>
      <img src={logoColour} className={`${className} dark:hidden`} draggable={false} {...imgProps(title)} />
      <img src={logoOnDark} className={`${className} hidden dark:block`} draggable={false} alt="" aria-hidden />
    </>
  );
}

/**
 * The towers alone, square. For places too small for the wordmark — the auth
 * pages render it at 32x32, where the full lockup is an illegible smudge.
 */
export function SafaqatMark({
  tone = 'colour',
  className = '',
  title,
}: {
  tone?: Tone;
  className?: string;
  title?: string;
}) {
  if (tone === 'onDark') {
    return <img src={markOnDark} className={className} draggable={false} {...imgProps(title)} />;
  }
  return (
    <>
      <img src={markColour} className={`${className} dark:hidden`} draggable={false} {...imgProps(title)} />
      <img src={markOnDark} className={`${className} hidden dark:block`} draggable={false} alt="" aria-hidden />
    </>
  );
}
